import { siteInfo, aboutContent, faqData } from "@/mocks/site-info";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import { useState } from "react";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== "") {
      setFormStatus("success");
      form.reset();
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const formData = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9q1gufnahb7382ov4ng", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
        setTimeout(() => setFormStatus("idle"), 3000);
      } else {
        const serverMsg =
          (parsed?.meta as Record<string, unknown>)?.message as string ||
          (parsed?.message as string) ||
          responseText ||
          "提交失败，请稍后再试。";
        setFormError(serverMsg.includes("spam") || serverMsg.includes("form data is spam")
          ? "提交失败，请稍后再试。"
          : serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试。");
      setFormStatus("error");
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={aboutContent.brandIntro}
        canonicalUrl="https://akeelah-guide.com/about"
      />
      <SchemaOrg type="about" />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-4" style={{ fontFamily: "var(--font-heading)" }}>
              关于我们
            </h1>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {aboutContent.brandIntro}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutContent.brandStory}
                </p>
              </div>

              <div className="mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  口碑数据
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {aboutContent.reputation.map((item, index) => (
                    <div
                      key={index}
                      className="text-center p-5 bg-background-100 border border-background-200/70 rounded-lg"
                    >
                      <p className="text-2xl md:text-3xl font-bold text-primary-500 mb-1" style={{ fontFamily: "var(--font-heading)" }}>
                        {item.metric}
                      </p>
                      <p className="text-xs md:text-sm text-foreground-500">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  核心价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aboutContent.values.map((value, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-5 bg-background-100 border border-background-200/70 rounded-lg"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-check-double-line text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-950 mb-1">{value.title}</h3>
                        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100 border-t border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
              <div className="space-y-4">
                {faqData.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                      <span className="text-sm md:text-base font-medium text-foreground-950 pr-4">
                        {faq.question}
                      </span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50 border-t border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                有任何问题或建议？欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="text-center p-5 bg-background-100 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-950">{siteInfo.email}</p>
                </div>
                <div className="text-center p-5 bg-background-100 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">联系电话</p>
                  <p className="text-sm font-medium text-foreground-950">{siteInfo.phone}</p>
                </div>
                <div className="text-center p-5 bg-background-100 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">办公地址</p>
                  <p className="text-sm font-medium text-foreground-950">{siteInfo.address}</p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form="true"
                id="contact-form"
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入邮件主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10"
                />

                <div>
                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === "submitting" ? "提交中..." : "发送消息"}
                  </button>
                </div>

                {formStatus === "success" && (
                  <div className="p-3 bg-accent-100 text-accent-500 rounded-md text-sm">
                    消息发送成功！我们会尽快回复您。
                  </div>
                )}
                {formStatus === "error" && formError && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}